class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;
    this.searchRequestId = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));

    this.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );

    this.addEventListener(
      "keyup",
      this.onKeyup.bind(this),
    );

    this.addEventListener(
      "keydown",
      this.onKeydown.bind(this),
    );
  }


  getQuery() {
    return this.input.value.trim();
  }


  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }


  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }


  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }


  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }


  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }


  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }


  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;

      case "ArrowDown":
        this.switchOption("down");
        break;

      case "Enter":
        this.selectOption();
        break;
    }
  }


  onKeydown(event) {
    /*
     * Prevent cursor movement in the search input
     * when using Arrow Up / Arrow Down.
     */

    if (
      event.code === "ArrowUp" ||
      event.code === "ArrowDown"
    ) {
      event.preventDefault();
    }
  }


  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp =
      direction === "up";

    const selectedElement =
      this.querySelector(
        '[aria-selected="true"]',
      );

    /*
     * Filter hidden duplicate page/article resources.
     */

    const allVisibleElements =
      Array.from(
        this.querySelectorAll(
          "li, button.predictive-search-item-button",
        ),
      ).filter(
        (element) =>
          element.offsetParent !== null,
      );

    let activeElementIndex = 0;

    if (
      moveUp &&
      !selectedElement
    ) {
      return;
    }

    let selectedElementIndex = -1;

    let i = 0;

    while (
      selectedElementIndex === -1 &&
      i <= allVisibleElements.length
    ) {
      if (
        allVisibleElements[i] ===
        selectedElement
      ) {
        selectedElementIndex = i;
      }

      i++;
    }

    this.statusElement.textContent = "";

    if (
      !moveUp &&
      selectedElement
    ) {
      activeElementIndex =
        selectedElementIndex ===
        allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;

    } else if (moveUp) {

      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (
      activeElementIndex ===
      selectedElementIndex
    ) {
      return;
    }

    const activeElement =
      allVisibleElements[
        activeElementIndex
      ];

    activeElement.setAttribute(
      "aria-selected",
      true,
    );

    if (selectedElement) {
      selectedElement.setAttribute(
        "aria-selected",
        false,
      );
    }

    this.input.setAttribute(
      "aria-activedescendant",
      activeElement.id,
    );
  }


  selectOption() {
    const selectedProduct =
      this.querySelector(
        '[aria-selected="true"] a, [aria-selected="true"] button',
      );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }


  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();

    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    const requestId = ++this.searchRequestId;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = Math.min(Number(this.dataset.limit) || 10, 10);
    const limitScope = this.dataset.limitScope;
    const smartTerms = this.buildSmartSearchTerms(searchTerm);

    this.fetchPredictiveSection(
      smartTerms[0],
      filterType,
      filterTypeQuery,
      limit,
      limitScope,
    )
      .then(async (primarySection) => {
        if (requestId !== this.searchRequestId) return;

        const primaryCount = this.getPredictiveProductCount(primarySection);
        const enoughResultsThreshold = Math.min(5, limit);

        /*
         * If Shopify already gives a healthy result set,
         * keep the original Athens result completely untouched.
         */
        if (primaryCount >= enoughResultsThreshold || smartTerms.length === 1) {
          const markup = primarySection.innerHTML;
          this.cachedResults[queryKey] = markup;
          this.renderSearchResults(markup);
          return;
        }

        /*
         * Only when the original query is weak do we try a few
         * generic query variations. Nothing here is category-specific.
         */
        const fallbackTerms = smartTerms.slice(1, 5);

        const fallbackRequests = fallbackTerms.map((term) =>
          this.fetchPredictiveSection(
            term,
            filterType,
            filterTypeQuery,
            limit,
            limitScope,
          ),
        );

        const fallbackResponses = await Promise.allSettled(fallbackRequests);

        if (requestId !== this.searchRequestId) return;

        const sections = [primarySection];

        fallbackResponses.forEach((response) => {
          if (response.status === "fulfilled" && response.value) {
            sections.push(response.value);
          }
        });

        const mergedSection = this.mergePredictiveSections(sections, limit);
        const markup = mergedSection.innerHTML;

        this.cachedResults[queryKey] = markup;
        this.renderSearchResults(markup);
      })
      .catch((error) => {
        if (requestId !== this.searchRequestId) return;

        this.close();
        console.error("Predictive search error:", error);
      });
  }


  /*
   * Build a small set of generic query variants.
   *
   * Why this helps:
   * Shopify predictive search applies partial-word matching only
   * to the LAST term of a multi-word query. Moving different terms
   * to the end can recover relevant products without changing the UI.
   */
  buildSmartSearchTerms(searchTerm) {
    const original = searchTerm.trim().replace(/\s+/g, " ");
    const terms = [];

    const addTerm = (term) => {
      if (!term) return;

      const clean = term.trim().replace(/\s+/g, " ");
      if (!clean) return;

      const exists = terms.some(
        (existing) => existing.toLowerCase() === clean.toLowerCase(),
      );

      if (!exists) terms.push(clean);
    };

    addTerm(original);

    /*
     * Normalize common specification spacing in both directions.
     * Examples: "16 GB" <-> "16GB", "144 Hz" <-> "144Hz".
     * This is generic and works across product categories.
     */
    const units =
      "kb|mb|gb|tb|hz|khz|mhz|ghz|w|kw|wh|mah|v|mv|a|ma|mm|cm|m|inch|inches";

    const compactUnits = original.replace(
      new RegExp(`(\\d+(?:[.,]\\d+)?)\\s+(${units})\\b`, "gi"),
      "$1$2",
    );
    addTerm(compactUnits);

    const splitUnits = original.replace(
      new RegExp(`(\\d+(?:[.,]\\d+)?)(?=(${units})\\b)`, "gi"),
      "$1 ",
    );
    addTerm(splitUnits);

    /*
     * Create a punctuation-normalized variation without destroying
     * model numbers that use hyphens. Slashes, commas and pipes are
     * commonly used as separators in supplier titles.
     */
    const normalizedSeparators = original.replace(/[\\/,|]+/g, " ");
    addTerm(normalizedSeparators);

    const tokens = original.split(/\s+/).filter(Boolean);

    if (tokens.length > 1) {
      const rankedTokens = tokens
        .map((token, index) => ({
          token,
          index,
          hasNumber: /\d/.test(token),
          length: token.length,
        }))
        .sort((a, b) => {
          if (a.hasNumber !== b.hasNumber) {
            return a.hasNumber ? -1 : 1;
          }
          return b.length - a.length;
        });

      /*
       * Put different meaningful tokens last. We prioritize numeric /
       * specification-like tokens because those often appear as 512GB,
       * 144Hz, 100W, etc. in product titles.
       */
      rankedTokens.forEach(({ index }) => {
        if (index === tokens.length - 1) return;

        const reordered = tokens.filter((_, tokenIndex) => tokenIndex !== index);
        reordered.push(tokens[index]);
        addTerm(reordered.join(" "));
      });
    }

    /*
     * Keep network usage controlled: original query + at most 6 variants.
     * getSearchResults currently uses at most the first 4 fallbacks.
     */
    return terms.slice(0, 7);
  }


  fetchPredictiveSection(
    searchTerm,
    filterType,
    filterTypeQuery,
    limit,
    limitScope,
  ) {
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    const url =
      `${routes.predictive_search_url}` +
      `?q=${query}` +
      `&resources[limit_scope]=${limitScope}` +
      `&resources[limit]=${limit}` +
      `&options[prefix]=last` +
      `&section_id=predictive-search` +
      `&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,vendor`;

    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        const parsedDocument = new DOMParser().parseFromString(text, "text/html");
        const section = parsedDocument.querySelector(
          "#shopify-section-predictive-search",
        );

        if (!section) {
          throw new Error("Predictive search section not found.");
        }

        return section;
      });
  }


  getPredictiveProductCount(section) {
    if (!section) return 0;

    return section.querySelectorAll(".predictive-search-list-item-product").length;
  }


  mergePredictiveSections(sections, maxProducts) {
  /*
   * Always use the ORIGINAL query as the base so Athens suggestions,
   * collections, pages, headings and the visible "Search for..." text
   * remain exactly tied to what the customer actually typed.
   */
  const merged = sections[0].cloneNode(true);

  let productList = merged.querySelector(
    "#predictive-search-results-item-product-list",
  );

  /*
   * If the original query returned zero products, Athens won't render
   * a product list. In that case, graft only the product section from
   * the strongest fallback into Athens' existing second results group.
   */
  if (!productList) {
    let bestProductSection = null;
    let bestProductCount = 0;

    sections.slice(1).forEach((section) => {
      const count =
        this.getPredictiveProductCount(section);

      const list = section.querySelector(
        "#predictive-search-results-item-product-list",
      );

      if (
        list &&
        count > bestProductCount
      ) {
        bestProductCount = count;

        bestProductSection =
          list.closest(
            ".predictive-search-results-group-section",
          );
      }
    });

    if (bestProductSection) {
      const resultGroups =
        merged.querySelectorAll(
          ".predictive-search-results-group",
        );

      const productsGroup =
        resultGroups[
          resultGroups.length - 1
        ];

      if (productsGroup) {
        productsGroup.prepend(
          bestProductSection.cloneNode(true),
        );

        productList =
          merged.querySelector(
            "#predictive-search-results-item-product-list",
          );

        const wrapper =
          merged.querySelector(
            ".predictive-search-results-group-wrapper",
          );

        const firstGroup =
          resultGroups[0];

        if (
          wrapper &&
          firstGroup &&
          firstGroup.querySelector(
            ".predictive-search-results-group-section",
          )
        ) {
          wrapper.classList.add(
            "predictive-search-results-group-wrapper-two-column",
          );
        }

        const resultsList =
          merged.querySelector(
            "#predictive-search-results-list",
          );

        if (resultsList) {
          resultsList.classList.remove(
            "predictive-search-results-list-empty",
          );
        }
      }
    }
  }

  if (!productList) {
    return merged;
  }

  /*
   * Build one stable identity for every product.
   *
   * We intentionally ignore:
   * - ?variant=
   * - tracking/query parameters
   * - hashes
   *
   * Example:
   * /products/wuben-g5
   * /products/wuben-g5?variant=123
   *
   * Both become:
   * wuben-g5
   */
  const getProductKey = (link) => {
    if (!link) {
      return "";
    }

    const href =
      link.getAttribute("href");

    if (!href) {
      return "";
    }

    try {
      const url =
        new URL(
          href,
          window.location.origin,
        );

      const productMatch =
        url.pathname.match(
          /\/products\/([^/?#]+)/i,
        );

      if (
        productMatch &&
        productMatch[1]
      ) {
        return decodeURIComponent(
          productMatch[1],
        ).toLowerCase();
      }

      return url.pathname
        .replace(/\/+$/, "")
        .toLowerCase();
    } catch (error) {
      return href
        .split(/[?#]/)[0]
        .replace(/\/+$/, "")
        .toLowerCase();
    }
  };

  const existingProducts =
    new Set();

  /*
   * First dedupe the ORIGINAL Shopify result list itself.
   * This also protects us if Shopify ever returns the
   * same product through slightly different URLs.
   */
  productList
    .querySelectorAll(
      ".predictive-search-list-item-product",
    )
    .forEach((product) => {
      const link =
        product.querySelector(
          "a[href]",
        );

      const productKey =
        getProductKey(link);

      if (!productKey) {
        return;
      }

      if (
        existingProducts.has(
          productKey,
        )
      ) {
        product.remove();
        return;
      }

      existingProducts.add(
        productKey,
      );
    });

  let currentCount =
    productList.querySelectorAll(
      ".predictive-search-list-item-product",
    ).length;

  /*
   * Merge fallback products while deduping
   * by PRODUCT HANDLE rather than raw URL.
   */
  sections
    .slice(1)
    .forEach((section) => {
      if (
        currentCount >= maxProducts
      ) {
        return;
      }

      section
        .querySelectorAll(
          ".predictive-search-list-item-product",
        )
        .forEach((product) => {
          if (
            currentCount >=
            maxProducts
          ) {
            return;
          }

          const link =
            product.querySelector(
              "a[href]",
            );

          const productKey =
            getProductKey(link);

          if (
            !productKey ||
            existingProducts.has(
              productKey,
            )
          ) {
            return;
          }

          existingProducts.add(
            productKey,
          );

          currentCount += 1;

          const clone =
            product.cloneNode(true);

          clone.id =
            `predictive-search-option-product-${currentCount}`;

          productList.appendChild(
            clone,
          );
        });
    });

  return merged;
}


  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        ".predictive-search-status",
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        "data-loading-text",
      );

    this.setLiveRegionText(
      this.loadingText,
    );

    this.setAttribute(
      "loading",
      true,
    );
  }


  setLiveRegionText(statusText) {
    this.statusElement.setAttribute(
      "aria-hidden",
      "false",
    );

    this.statusElement.textContent =
      statusText;

    setTimeout(() => {
      this.statusElement.setAttribute(
        "aria-hidden",
        "true",
      );
    }, 1000);
  }


  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      "results",
      true,
    );

    this.setLiveRegionResults();

    this.open();
  }


  setLiveRegionResults() {
    this.removeAttribute(
      "loading",
    );

    this.setLiveRegionText(
      this.querySelector(
        "[data-predictive-search-live-region-count-value]",
      ).textContent,
    );
  }


  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document
        .querySelector(
          ".section-header",
        )
        .getBoundingClientRect()
        .bottom;

    return this.resultsMaxHeight;
  }


  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute(
      "open",
      true,
    );

    this.input.setAttribute(
      "aria-expanded",
      true,
    );

    if (!this.isOpen) {
      const event =
        new CustomEvent(
          "search:open",
        );

      document.dispatchEvent(
        event,
      );
    }

    this.isOpen = true;
  }


  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";

      this.removeAttribute(
        "results",
      );
    }

    const selected =
      this.querySelector(
        '[aria-selected="true"]',
      );

    if (selected) {
      selected.setAttribute(
        "aria-selected",
        false,
      );
    }

    this.input.setAttribute(
      "aria-activedescendant",
      "",
    );

    this.removeAttribute(
      "open",
    );

    this.input.setAttribute(
      "aria-expanded",
      false,
    );

    this.resultsMaxHeight =
      false;

    this.predictiveSearchResults.removeAttribute(
      "style",
    );

    if (this.isOpen) {
      const event =
        new CustomEvent(
          "search:close",
        );

      document.dispatchEvent(
        event,
      );
    }

    this.isOpen = false;
  }
}


customElements.define(
  "predictive-search",
  PredictiveSearch,
);